<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <assessment-helper></assessment-helper>
        <section id="paragraph-container">
          <blinking-cursor-text
            :text="currentRound.paragraph"
            :text-step="progress.roundIndex"
          ></blinking-cursor-text>
        </section>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import ActivityManager from 'mixins/ActivityManager';
import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import AssessmentHelper from 'components/utils/AssessmentHelper.vue';

export default {
  mixins: [ActivityManager, AssetLoader, ErrorLogger, TypingActivityHelper],

  components: {
    ActivityStage,
    AssessmentHelper,
    BlinkingCursorText
  },

  data() {
    return {
      activityName: 'Paragraph'
    };
  },

  methods: {
    async loadActivity() {
      this.createRounds({ stepIndicator: 'paragraph' });
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        roofs
      };
    },

    startActivity() {
      this.setRound();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);
    },

    setRound() {
      this.setupNewRound({ stepCallback: this.stepCallback });
      this.enableKeyboard();
      this.$nextTick(() => {
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    stepCallback() {
      return this.currentRound.paragraph;
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      const t = new TimelineLite();
      this.sndBell();

      t.delay(0.8).call(() => {
        if (this.isLastRound) {
          this.handleActivityEnd();
        } else {
          t.delay(1)
            .to('.activity-stage', 0.8, { opacity: 0 })
            .call(this.setRound);
        }
      });
    },

    handleActivityEnd() {
      this.hideKeyboard();
      TweenMax.delayedCall(1, this.completeActivity);
    }
  },
  watch: {
    expectedKey(newVal) {
      const paragraph = this.currentRound.paragraph;
      const instructionPrefix = `Type the paragraph -`;

      if (!paragraph || `${instructionPrefix} ${paragraph}` === this.currentScreenReaderAlert)
        return;

      this.rerenderScreenReaderAlert(`${instructionPrefix} ${paragraph}`);
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';

#paragraph-container {
  position: relative;
  height: 60%;
  font-size: 1.5em;
  display: flex;
  align-items: center;
  justify-items: center;
  box-sizing: border-box;

  p {
    margin: auto;
    font-size: 1.1em;
    width: 65%;
    text-align: justify;
  }
}

.tablet {
  #paragraph-container {
    align-items: start;
    justify-items: start;

    p {
      margin: 0 auto;
    }
  }
}
</style>
